import React, { useMemo, useState } from 'react';
import { FiLayers, FiTag, FiChevronDown } from 'react-icons/fi';

/**
 * Reusable Main Category + Category checkbox filter with smart cascading.
 *
 * Props:
 *   categories       - array of { id, name, color, main_category_id }
 *   mainCategories   - array of { id, name, color }
 *   selectedMainIds  - array of main category IDs currently checked (default: all)
 *   selectedCatIds   - array of category IDs currently checked (default: all)
 *   onChange         - ({ mainIds, catIds }) => void  — fires on any change
 *   compact          - bool — render in a more compact horizontal layout
 *
 * Behavior:
 *   - Checking/unchecking a main category checks/unchecks all its child categories
 *   - Categories list only shows children of currently-checked main categories
 *   - "Unassigned" virtual main category groups categories with main_category_id = null
 */
const CategoryFilter = ({
  categories = [],
  mainCategories = [],
  selectedMainIds = null,
  selectedCatIds = null,
  onChange,
  compact = false,
  collapsible = true,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  // Build the effective main category list (real ones + a virtual "Unassigned" if any cats lack a main)
  const hasUnassigned = useMemo(
    () => categories.some(c => !c.main_category_id),
    [categories]
  );
  const effectiveMains = useMemo(() => {
    const list = mainCategories.map(m => ({ ...m, _virtual: false }));
    if (hasUnassigned) list.push({ id: '__unassigned__', name: 'Uncategorized', color: '#6b7280', _virtual: true });
    return list;
  }, [mainCategories, hasUnassigned]);

  // Default selections: everything checked
  const mainIds = selectedMainIds ?? effectiveMains.map(m => m.id);
  const catIds  = selectedCatIds ?? categories.map(c => c.id);

  const isMainChecked = (id) => mainIds.includes(id);
  const isCatChecked  = (id) => catIds.includes(id);

  const catsForMain = (mainId) => {
    if (mainId === '__unassigned__') return categories.filter(c => !c.main_category_id);
    return categories.filter(c => String(c.main_category_id) === String(mainId));
  };

  // Visible categories = those whose main is currently checked
  const visibleCats = useMemo(() => {
    return categories.filter(c => {
      const mid = c.main_category_id ? c.main_category_id : '__unassigned__';
      return mainIds.some(id => String(id) === String(mid));
    });
  }, [categories, mainIds]);

  const toggleMain = (mainId) => {
    const childIds = catsForMain(mainId).map(c => c.id);
    let newMains, newCats;
    if (isMainChecked(mainId)) {
      // Uncheck main + uncheck all its children
      newMains = mainIds.filter(id => String(id) !== String(mainId));
      newCats  = catIds.filter(id => !childIds.some(cid => String(cid) === String(id)));
    } else {
      // Check main + check all its children
      newMains = [...mainIds, mainId];
      newCats  = Array.from(new Set([...catIds, ...childIds]));
    }
    onChange?.({ mainIds: newMains, catIds: newCats });
  };

  const toggleCat = (catId) => {
    let newCats;
    if (isCatChecked(catId)) {
      newCats = catIds.filter(id => String(id) !== String(catId));
    } else {
      newCats = [...catIds, catId];
    }
    onChange?.({ mainIds, catIds: newCats });
  };

  const allOn = () => {
    onChange?.({
      mainIds: effectiveMains.map(m => m.id),
      catIds: categories.map(c => c.id),
    });
  };
  const allOff = () => {
    onChange?.({ mainIds: [], catIds: [] });
  };

  const row = (label, color, checked, onClick, key) => (
    <label
      key={key}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', borderRadius: 6,
        background: checked ? '#fff' : '#fafafa',
        border: `1px solid ${checked ? '#e5e7eb' : '#f3f4f6'}`,
        fontSize: 13, fontWeight: 500,
        color: checked ? '#111827' : '#9ca3af',
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = checked ? '#f9fafb' : '#f3f4f6'; }}
      onMouseLeave={e => { e.currentTarget.style.background = checked ? '#fff' : '#fafafa'; }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onClick}
        style={{ margin: 0, cursor: 'pointer', accentColor: color }}
      />
      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
      <span style={{ flex: 1, textDecoration: checked ? 'none' : 'line-through' }}>{label}</span>
    </label>
  );

  const headerRow = (icon, text, showAllNone) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {icon} {text}
      </span>
      {showAllNone && (
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button type="button" onClick={allOn}
            style={{ fontSize: 11, padding: '3px 10px', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}>All</button>
          <button type="button" onClick={allOff}
            style={{ fontSize: 11, padding: '3px 10px', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', color: '#6b7280' }}>None</button>
        </span>
      )}
    </div>
  );

  const anyFiltered = (selectedMainIds !== null) || (selectedCatIds !== null);

  const body = (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
      padding: compact ? '10px 14px' : '14px 18px',
      marginBottom: 16,
    }}>
      {headerRow(<FiLayers size={11} />, 'Main Category:', effectiveMains.length > 0)}
      {effectiveMains.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>(none configured)</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6 }}>
          {effectiveMains.map(m =>
            row(m.name, m.color || '#6b7280', isMainChecked(m.id), () => toggleMain(m.id), `m-${m.id}`)
          )}
        </div>
      )}

      {visibleCats.length > 0 && (
        <div style={{ borderTop: '1px dashed #e5e7eb', marginTop: 12, paddingTop: 10 }}>
          {headerRow(<FiTag size={11} />, 'Category:', false)}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {visibleCats.map(c =>
              row(c.name, c.color || '#6b7280', isCatChecked(c.id), () => toggleCat(c.id), `c-${c.id}`)
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (!collapsible) return body;

  return (
    <div className="m-collapse">
      <button
        type="button"
        className="m-collapse-btn"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>Categories{anyFiltered && <span className="m-collapse-dot" />}</span>
        <FiChevronDown size={16} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      <div className={open ? 'm-collapse-body is-open' : 'm-collapse-body'}>
        {body}
      </div>
    </div>
  );
};

export default CategoryFilter;
